import { useContext, useEffect, useMemo, useState } from 'react'
import ReactDatePicker from 'react-datepicker'
import { Link } from 'react-router'
import { observer } from 'mobx-react-lite'
import moment from 'moment-timezone'

//evertel
import {
    Badge, Button, Col, Icon, Row, Text,  InfoBox,
    TanTable, TanTableColumnDef,
    SecuredFile, SecuredFileData
} from '@evertel/web/ui'
import { APIDataAudit } from '@evertel/types'
import { useService } from '@evertel/di'
import { AuditsSearchController } from '@evertel/web/admin'
import { RoomContext } from '../RoomContext'

interface RoomExportsTableProps {
    roomId: number
}

const RoomExportsTable: React.FC<RoomExportsTableProps> = observer(({
    roomId
}) => {
    const { roomController } = useContext(RoomContext)
    const room = roomController.room
    const auditsSearchController = useService(AuditsSearchController, [roomId])

    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
        moment().subtract(30, 'days').startOf('day').toDate(),
        moment().endOf('day').toDate()
    ])
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [newAuditId, setNewAuditId] = useState<number | null>(null)
    const [newAuditStatus, setNewAuditStatus] = useState<string | null>(null)

    useEffect(() => {
        auditsSearchController.init(roomId, 'room')
        auditsSearchController.setLimit(8)
    }, [roomId])

    useEffect(() => {
        auditsSearchController.debouncedSearch()
    }, [
        auditsSearchController,
        auditsSearchController.page,
        auditsSearchController.limit,
        auditsSearchController.sortBy,
        auditsSearchController.sortOrder
    ])

    // Effect to check audit status
    useEffect(() => {
        let intervalId: NodeJS.Timeout | null = null

        if (newAuditId) {
            const checkStatus = async () => {
                const status = await auditsSearchController.checkAuditStatus(newAuditId)
                setNewAuditStatus(status)

                if (status === 'complete' || status === 'failed') {
                    auditsSearchController.setSorting('id', 'desc')
                    auditsSearchController.debouncedSearch()
                    setNewAuditId(null)
                    if (intervalId) clearInterval(intervalId)
                }
            }

            intervalId = setInterval(checkStatus, 3000)
        }

        return () => {
            if (intervalId) clearInterval(intervalId)
        }
    }, [newAuditId, auditsSearchController])

    const handleRunAudit = async () => {
        if (!dateRange[0] || !dateRange[1]) {
            setError('Please select a valid date range')
            return
        }

        setIsSubmitting(true)
        setError(null)

        try {
            const auditData = {
                dateRangeStart: dateRange[0],
                dateRangeEnd: dateRange[1],
                description: `Room export from ${moment(dateRange[0]).format('MM/DD/YYYY')} to ${moment(dateRange[1]).format('MM/DD/YYYY')} for ${room?.name}`,
                timeZone: moment.tz.guess()
            }

            const result = await auditsSearchController.runRoomAudit(auditData)
            setNewAuditId(result.id)
            setNewAuditStatus('processing')
            auditsSearchController.setSorting('id', 'desc')
            auditsSearchController.debouncedSearch()
        } catch (err) {
            setError(err?.message || 'Failed to create room export')
        } finally {
            setIsSubmitting(false)
        }
    }

    const columns = useMemo<TanTableColumnDef<APIDataAudit>[]>(() => [
        {
            accessorKey: 'createdTime',
            header: 'Date Run',
            size: 130,
            enableSorting: true,
            meta: {
                tdProps: {
                    colSpan: 2
                }
            },
            cell: ({ row }) => {
                const time = moment(row.original.createdTime).format('MM/DD/YYYY HH:mm')
                const file = row.original
                let fileLink
                if (!file.fileUrl || !file.fileName) {
                    fileLink =  ' - '
                } else {
                    file.fileName = file.fileName.replace(/(\d{4}-\d{2}-\d{2})T/, '$1 ')
                    file.fileUrl = auditsSearchController.roomAuditDownloadLink(row.original.id)
                    fileLink = <SecuredFile data={file as SecuredFileData} />
                }
                return (
                    <div>
                        {time}
                        <hr className="m-2" />
                        {fileLink}
                    </div>
                )
            }
        },
        {
            accessorKey: 'fileName',
            header: 'Audit Report',
            size: 150,
            meta: {
                tdProps: {
                    style: {
                        display: 'none'
                    }
                }
            },
            cell: ({ row }) => { /** */ }
        },
        {
            accessorKey: 'createdBy',
            header: 'Run By',
            size: '30%',
            cell: ({ row }) => {
                return (
                    <Link style={{fontSize: '.8rem'}} className="btn btn-link" to={`/manage/user/${row.original.createdBy.id}`}>
                        {row.original.createdBy.firstName + ' ' + row.original.createdBy.lastName}
                    </Link>
                )
            }
        },
        {
            accessorKey: 'status',
            header: 'Status',
            size: 90,
            enableSorting: true,
            meta: {align: 'right'},
            cell: ({ getValue, row }) => {
                const status = getValue() as string
                let color = 'light'
                switch (status) {
                    case 'complete': color = 'success'; break
                    case 'processing': color = 'warning'; break
                    case 'failed': color = 'danger'; break
                    case 'archived': color = 'secondary'; break
                }

                return (
                    <span style={{fontSize: '.8rem'}}>
                        <Badge color={color} className="text-capitalize ms-2">{status}</Badge>
                    </span>
                )
            }
        }
    ], [])

    return (
        <>
            {newAuditStatus && (
                <InfoBox
                    color={(newAuditStatus === 'complete') ? 'success' : (newAuditStatus === 'failed') ? 'danger' : 'warning'}
                    className="mb-4"
                >
                    <h4>
                        {newAuditStatus === 'complete' ? 'Export Complete!' :
                            newAuditStatus === 'failed' ? 'Export Failed' :
                                'Processing Export...'}
                    </h4>
                    <p>
                        {newAuditStatus === 'complete' ? 'Your room export is ready to download.' :
                            newAuditStatus === 'failed' ? 'There was an error creating your export. Please try again.' :
                                'Please wait while your room export is being prepared...'}
                    </p>
                </InfoBox>
            )}

            {error && (
                <InfoBox color="danger" className="mb-4">
                    {error}
                </InfoBox>
            )}

            <Row className="mt-4">
                <Text bold>
                    Date Range
                </Text>
            </Row>
            <Row className="mt-2">
                <Col>
                    <ReactDatePicker className="form-control"
                        selected={dateRange[0]}
                        onChange={(date: Date | null) => setDateRange([date, date <= dateRange[1] ? dateRange[1] : date])}
                        showTimeSelect
                        timeFormat="HH:mm"
                        timeIntervals={15}
                        dateFormat="MM/dd/yyyy HH:mm"
                        placeholderText="From..."
                        startDate={dateRange[0]}
                        endDate={dateRange[1]}
                        minDate={moment().subtract(6, 'years').toDate()}
                        maxDate={dateRange[1] || new Date()}
                        calendarClassName="datepicker"
                    />
                </Col>
                <Col className="ms-2">
                    <ReactDatePicker className="form-control"
                        selected={dateRange[1]}
                        onChange={(date: Date | null) => setDateRange([dateRange[0], date >= dateRange[0] ? date : dateRange[0]])}
                        showTimeSelect
                        timeFormat="HH:mm"
                        timeIntervals={15}
                        dateFormat="MM/dd/yyyy HH:mm"
                        placeholderText="To..."
                        startDate={dateRange[0]}
                        endDate={dateRange[1]}
                        minDate={dateRange[0]}
                        maxDate={moment().endOf('day').toDate()}
                        popperPlacement="bottom-end"
                        calendarClassName="datepicker"
                    />
                </Col>
            </Row>
            <Row
                align="right"
                className="mt-2"
                style={{fontSize: '.9rem'}}
            >
                <Button
                    color="primary"
                    onClick={handleRunAudit}
                    loading={isSubmitting}
                    disabled={isSubmitting || !dateRange[0] || !dateRange[1]}
                >
                    <Icon
                        name="file-export"
                        className="me-2"
                    />
                    <Text>
                        New Room Export
                    </Text>
                </Button>
            </Row>
            <Row className="mt-5">
                <TanTable
                    data={auditsSearchController.audits}
                    columns={columns}
                    options={{
                        responsive: true,
                        pageSize: auditsSearchController.limit,
                        pageNumber: auditsSearchController.page,
                        sortName: auditsSearchController.sortBy,
                        sortOrder: auditsSearchController.sortOrder,
                        onPageChange: (page) => auditsSearchController.setPage(page),
                        onPageSizeChange: (pageSize) => auditsSearchController.setLimit(pageSize),
                        onSortChange: (sortName, sortOrder) => auditsSearchController.setSorting(sortName, sortOrder),
                        onGlobalSearchChange: (value) => auditsSearchController.setSearchTerm(value),
                        globalSearchDebounce: 0,
                        emptyState: (
                            <div className="text-center">
                                Room audits will be shown here
                            </div>
                        )
                    }}
                    loading={auditsSearchController.isLoading}
                    rowCount={1}
                    remote={true}
                    style={{
                        fontSize: '.8rem'
                    }}
                />
            </Row>
        </>
    )
})

export { RoomExportsTable }
